import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Button,
  Switch,
  LinearProgress,
} from "@material-ui/core";
import InputText from "components/_refactor/text/input";
import MaskAndValid from "utils/maskAndValid";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

const formularioVazio = {
  sellerCode: "",
  sellerName: "",
  cnpj: "",
  idSeta: "",
  idMarca: "",
  idKarsten: "",
  isActive: true,
  piNeomodePipelineAtivo: false,
};

export default function DialogSeller({ open, onClose, onSaved, seller }) {
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(formularioVazio);

  useEffect(() => {
    if (!open) return;

    if (!seller?.Id) {
      setForm(formularioVazio);
      return;
    }

    async function getSeller() {
      setLoading(true);
      try {
        const response = await Api.get("get-seller", "order", `?id=${seller.Id}`, true);

        if (response?.Success === false && response?.ValidationMessage) {
          snackbar.showMessage(response.ValidationMessage, { severity: "error" });
          return;
        }

        const detalhe = response?.Result || seller;

        setForm({
          sellerCode: detalhe.SellerCode || "",
          sellerName: detalhe.SellerName || "",
          cnpj: detalhe.Cnpj ? MaskAndValid.maskCNPJ(detalhe.Cnpj) : "",
          idSeta: detalhe.IdSeta || "",
          idMarca: detalhe.IdMarca ?? "",
          idKarsten: detalhe.IdKarsten ?? "",
          isActive: detalhe.IsActive !== false,
          piNeomodePipelineAtivo: !!detalhe.PiNeomodePipelineAtivo,
        });
      } catch (error) {
        snackbar.showMessage(error?.toString() || "Erro ao carregar o seller.", {
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }

    getSeller();
  }, [seller, open, snackbar]);

  const updateForm = (campo, valor) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const handleSave = async () => {
    if (!form.cnpj?.trim() || !MaskAndValid.validateCNPJ(form.cnpj)) {
      snackbar.showMessage("CNPJ é obrigatório e deve ser válido.", { severity: "error" });
      return;
    }
    if (form.sellerName?.length > 255) {
      snackbar.showMessage("Nome deve ter no máximo 255 caracteres.", {
        severity: "error",
      });
      return;
    }
    if (form.cnpj?.trim() && !MaskAndValid.validateCNPJ(form.cnpj)) {
      snackbar.showMessage("CNPJ inválido.", { severity: "error" });
      return;
    }
    setSaving(true);
    try {
      const cnpjLimpo = form.cnpj ? form.cnpj.replace(/[^\d]+/g, "") : "";
      const body = {
        sellerCode: cnpjLimpo,
        sellerName: form.sellerName?.trim() || "",
        cnpj: cnpjLimpo,
        idSeta: form.idSeta === "" ? "" : Number(form.idSeta),
        idMarca: form.idMarca === "" ? "" : Number(form.idMarca),
        idKarsten: form.idKarsten === "" ? "" : Number(form.idKarsten),
        isActive: form.isActive,
        piNeomodePipelineAtivo: form.piNeomodePipelineAtivo,
      };

      let response;

      if (seller?.Id) {
        body.id = seller.Id;
        response = await Api.put("update-seller", "order", body, true);
      } else {
        response = await Api.post("create-seller", "order", body, true);
      }

      if (response?.Success === false && response?.ValidationMessage) {
        snackbar.showMessage(response.ValidationMessage, { severity: "error" });
        return;
      }

      snackbar.showMessage(
        seller?.Id ? "Seller atualizado com sucesso." : "Seller criado com sucesso.",
        { severity: "success" }
      );

      if (onSaved) onSaved();
      else onClose();
    } catch (error) {
      snackbar.showMessage(error?.toString() || "Erro ao salvar seller.", {
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const desabilitado = saving || loading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="dialog-seller-title"
      maxWidth="md"
      fullWidth
    >
      {loading && <LinearProgress />}
      <DialogContent style={{ overflowY: "hidden" }}>
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 600, marginBottom: 8, marginTop: 8 }}
        >
          Dados Básicos
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputText
              label="CNPJ"
              value={form.cnpj}
              onChange={(e) => updateForm("cnpj", MaskAndValid.maskCNPJ(e.target.value))}
              placeholder="XX.XXX.XXX/XXXX-XX"
              disabled={desabilitado}
              error={!!form.cnpj && !MaskAndValid.validateCNPJ(form.cnpj)}
              errorMessage={
                form.cnpj && !MaskAndValid.validateCNPJ(form.cnpj) ? "CNPJ inválido" : ""
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputText
              label="Nome da Loja"
              value={form.sellerName}
              onChange={(e) => updateForm("sellerName", e.target.value)}
              placeholder="Nome da Loja"
              disabled={desabilitado}
            />
          </Grid>
        </Grid>

        <Typography
          variant="subtitle1"
          style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}
        >
          Identificadores
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <InputText
              label="ID Marca"
              value={form.idMarca}
              onChange={(e) =>
                updateForm("idMarca", e.target.value.replace(/[^\d]+/g, ""))
              }
              placeholder="ID Marca"
              disabled={desabilitado}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputText
              label="ID Seta"
              value={form.idSeta}
              onChange={(e) =>
                updateForm("idSeta", e.target.value.replace(/[^\d]+/g, ""))
              }
              placeholder="ID Seta"
              disabled={desabilitado}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputText
              label="ID Karsten"
              value={form.idKarsten}
              onChange={(e) =>
                updateForm("idKarsten", e.target.value.replace(/[^\d]+/g, ""))
              }
              placeholder="ID Karsten"
              disabled={desabilitado}
            />
          </Grid>
        </Grid>

        <Typography
          variant="subtitle1"
          style={{ fontWeight: 600, marginBottom: 8, marginTop: 24 }}
        >
          Configurações
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Grid container justify="space-between" alignItems="center">
              <Typography>Seller Ativo</Typography>
              <Switch
                checked={form.isActive}
                onChange={(e) => updateForm("isActive", e.target.checked)}
                disabled={desabilitado}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Grid container justify="space-between" alignItems="center">
              <Typography>Pipeline Neomode Ativo</Typography>
              <Switch
                checked={form.piNeomodePipelineAtivo}
                onChange={(e) => updateForm("piNeomodePipelineAtivo", e.target.checked)}
                disabled={desabilitado}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} color="primary" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={desabilitado}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
